/**
 * Stereo Image Skew chart — mobile's light-themed Canvas2D counterpart to
 * web's `web/src/components/room/charts/stereoImageSkewDraw.ts`. Same
 * geometry (`computeStereoImageSkewGeometry`), different ink — see
 * `./_shared.ts`'s module doc.
 *
 * Twin-axis overlay, one log-frequency x-axis shared by two independent
 * y-scales (level diff in dB, time diff in ms) — `drawLogFreqAxisGrid`
 * only paints the left (level) axis, so the right (time) axis' tick labels
 * get their own small pass, mirroring web's draw file. Per the app's
 * ink-weight convention the two series are told apart by weight/dash, not
 * hue: level diff is the full-weight solid line (the more audible of the
 * two effects), time diff is a lighter dashed line.
 */

import type { ChartPad } from '../layout';
import { computeStereoImageSkewGeometry } from '../stereoImageSkew';
import { setupCanvas, drawEmptyMessage, drawLogFreqAxisGrid, drawSeriesLine, INK } from './_shared';

export const STEREO_IMAGE_SKEW_PAD: ChartPad = { t: 14, r: 40, b: 26, l: 40 };

const TIME_DASH: number[] = [5, 3];

export interface DrawStereoImageSkewOpts {
  freqs: number[];
  magLeft: number[];
  magRight: number[];
  phaseLeft: number[];
  phaseRight: number[];
  width: number;
  height: number;
  fMin?: number;
  fMax?: number;
}

export function drawStereoImageSkewCanvas(canvas: HTMLCanvasElement, opts: DrawStereoImageSkewOpts): void {
  const s = setupCanvas(canvas, opts.width, opts.height);
  if (!s) return;
  const { ctx, W, H } = s;

  const geo = computeStereoImageSkewGeometry({
    freqs: opts.freqs,
    magLeft: opts.magLeft,
    magRight: opts.magRight,
    phaseLeft: opts.phaseLeft,
    phaseRight: opts.phaseRight,
    width: opts.width,
    height: opts.height,
    pad: STEREO_IMAGE_SKEW_PAD,
    fMin: opts.fMin,
    fMax: opts.fMax,
    measureWidth: (label) => ctx.measureText(label).width,
  });

  if (geo.empty) {
    drawEmptyMessage(ctx, W, H, 'No frequency response data yet');
    return;
  }

  drawLogFreqAxisGrid(ctx, geo.frame, geo.xTicks, geo.yTicksLevel);

  // Right-axis (time-diff, ms) tick labels — drawLogFreqAxisGrid only
  // paints the left (level-diff, dB) axis.
  ctx.textAlign = 'left';
  ctx.fillStyle = INK.label;
  for (const t of geo.yTicksTime) {
    ctx.fillText(t.label, geo.frame.plotX + geo.frame.plotW + 4, geo.frame.plotY + t.y + 3);
  }

  drawSeriesLine(ctx, geo.frame, geo.timePoints, INK.label, 1.4, TIME_DASH);
  drawSeriesLine(ctx, geo.frame, geo.levelPoints, INK.primary, 2.0);
}
